import { InterpolatePrismicDocumentError } from '../../utils/interpolatePrismicDocument/InterpolatePrismicDocumentError'
import { LoggerTeam, logger } from '@dtx-company/logger'
import { PrismicDocument } from '@prismicio/client'
import { RenderStringContext } from '@dtx-company/true-common/src/utils/strings/renderString'
import { interpolatePrismicDocument } from '../../utils/interpolatePrismicDocument/interpolatePrismicDocument'
import { useMemo } from 'react'
import stableHash from 'stable-hash'

function serialize<DocType extends PrismicDocument = PrismicDocument>(
  doc: DocType | undefined | (() => DocType | undefined),
  context: RenderStringContext
): [string, DocType | undefined] {
  if (typeof doc === 'function') {
    try {
      doc = doc()
    } catch (error) {
      // Like with useSWR, assume an error means deps are not loaded yet
      doc = undefined
    }
  }

  return [stableHash({ doc, context }), doc]
}

/**
  Interpolates the given prismic document with the given context object.
  The result may be used in place of the original document.

  Any errors are logged to DataDog under the specified team and the interpolation will
  then be re-done without errors so that the page does not fail completely when
  there are mistakes made in the prismic template.

  A function may be provided to delay providing the doc until the context has fully
  loaded in order to avoid errors from the context not having the value yet.

  @see interpolatePrismicDocument
  @see renderString
*/
export function usePrismicDocumentInterpolation<DocType extends PrismicDocument = PrismicDocument>(
  prismicDocument: DocType | undefined | (() => DocType | undefined),
  context: RenderStringContext,
  { team }: { team: LoggerTeam }
): DocType | undefined {
  const [hash, doc] = serialize(prismicDocument, context)

  return useMemo(() => {
    if (!doc) return
    try {
      return interpolatePrismicDocument(doc, context, { throwOnError: true })
    } catch (error) {
      if (error instanceof InterpolatePrismicDocumentError) {
        // Log the error but allow the page to still load, unknown filters
        // will be ignored and unknown placeholders will be left blank
        logger.logError(error, { team })
        return interpolatePrismicDocument(doc, context, { throwOnError: false })
      } else {
        throw error
      }
    }
    // The hash is a unique value that will change if the result of the doc or context changes
    // but using the literal doc or context would trigger unnecessary reloads here since they'll
    // be a different object/function on every re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash])
}
